import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, Col, Form, Row, Card, Accordion } from "react-bootstrap";
import axios from "../../../api/axios";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import Select from 'react-select';
import makeAnimated from "react-select/animated";
import Header from "../../../layouts/Header";

const EditOutlet = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { outletData } = location.state || {};
    
    const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
    const [skin, setSkin] = useState(currentSkin);
    const animatedComponents = makeAnimated();
    
    const accessToken = localStorage.getItem("authToken");
    const [isEditBtnLoading, setIsEditBtnLoading] = useState(false);
    const [groups, setGroups] = useState([]);
    const [purchaseData, setPurchaseData] = useState([]);
    const [foodUnit, setFoodUnit] = useState([]);
    const [beverageUnit, setBeverageUnit] = useState([]);
    const [centralUnit, setCentralUnit] = useState([]);
    const [activeAccordion, setActiveAccordion] = useState('0');
    
    const [editedData, setEditedData] = useState({
        _id: outletData?._id || '',
        name: outletData?.name || '',
        email: outletData?.email || '',
        phone: outletData?.phone || '',
        address: outletData?.address || '',
        pincode: outletData?.pincode || '',
        city: outletData?.city || '',
        state: outletData?.state || '',
        gstin: outletData?.gstin || '',
        country: outletData?.country || '',
        type: outletData?.type || '',
        latitude: outletData?.coordinates?.latitude || '',
        longitude: outletData?.coordinates?.longitude || '',
        openingTime: outletData?.openingTime || '',
        closingTime: outletData?.closingTime || '',
        status: outletData?.status || '',
        fssai: outletData?.fssai || '',
        minimumDeliveryRadius: outletData?.minimumDeliveryRadius || '',
        orderAcceptType: outletData?.orderAcceptType || '',
        isEventOutlet: outletData?.isEventOutlet || false,
        isOutletClosed: outletData?.isOutletClosed || false,
        menuGroup: outletData?.menuGroup?._id || '',
        paymentType: outletData?.paymentType || '',
        foodUnitId: outletData?.foodUnitId || '',
        beverageUnitId: outletData?.beverageUnitId || '',
        centralUnitId: outletData?.centralUnitId || '',
        pineLabCredentials: {
            clientId: outletData?.pineLabCredentials?.clientId || '',
            merchantId: outletData?.pineLabCredentials?.merchantId || '',
            securityToken: outletData?.pineLabCredentials?.securityToken || '',
            storeId: outletData?.pineLabCredentials?.storeId || '',
        },
        platForms: Array.isArray(outletData?.platForms) ? outletData.platForms : [],
    });

    const [editValidationErrors, setEditValidationErrors] = useState({
        name: "",
        email: "",
        phone: "",
        address: "",
        pincode: "",
        city: "",
        state: "",
        country: "",
    });

    // Custom styles for react-select - more compact
    const customSelectStyles = {
        control: (base) => ({
            ...base,
            border: '1px solid #dee2e6',
            borderRadius: '0.375rem',
            minHeight: '32px',
            fontSize: '0.875rem',
            '&:hover': {
                borderColor: '#86b7fe',
            },
        }),
        placeholder: (base) => ({
            ...base,
            color: '#6c757d',
            fontSize: '0.875rem',
        }),
        menu: (base) => ({
            ...base,
            fontSize: '0.875rem',
        }),
    };

    const foodUnitOptions = foodUnit?.map((pu) => ({
        label: pu?.name,
        value: pu?._id
    }));

    const beverageUnitOptions = beverageUnit?.map((pu) => ({
        label: pu?.name,
        value: pu?._id
    }));

    const centralUnitOptions = centralUnit?.map((pu) => ({
        label: pu?.name,
        value: pu?._id
    }));

    const getMenuGroup = async () => {
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
            };
            const response = await axios.get("menu-group", { headers });
            if (response?.data?.status === true) {
                setGroups(response?.data?.data?.menuGroup);
            }
        } catch (error) {
            if (error.response && error.response?.data?.status === false) {
                let errorMessage = error.response.data.error;
                toast.error(errorMessage);
            }
        }
    };

    const getProcurementUnits = async () => {
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`
            }
            const response = await axios.get(`procurement-units/list?page=1&limit=500`, { headers });
            setPurchaseData(response?.data?.data?.procurementUnits);
            let foodU = response?.data?.data?.procurementUnits.filter((fu) => (fu?.type === 'FOOD'))
            let beverageU = response?.data?.data?.procurementUnits.filter((fu) => (fu?.type === 'BEVERAGE'))
            let centralU = response?.data?.data?.procurementUnits.filter((fu) => (fu?.type === 'CENTRAL'))

            setFoodUnit(foodU);
            setBeverageUnit(beverageU);
            setCentralUnit(centralU);
        } catch (error) {
            console.log("Error in fetching purchase unit list :", error);
        }
    }

    const handleEditChange = (e, platformIndex = null) => {
        const { name, value, type, checked } = e.target;

        if (platformIndex !== null) {
            setEditedData((prevState) => {
                const updatedPlatforms = [...prevState.platForms];

                if (!updatedPlatforms[platformIndex]) {
                    updatedPlatforms[platformIndex] = { name: '', webLink: '' };
                }

                if (name === "platformName" || name === "platformWeblink") {
                    updatedPlatforms[platformIndex][name === "platformName" ? "name" : "webLink"] = value;
                }

                return {
                    ...prevState,
                    platForms: updatedPlatforms,
                };
            });
        } else if (name.includes(".")) {
            const [parentKey, childKey] = name.split(".");
            setEditedData((prevState) => ({
                ...prevState,
                [parentKey]: {
                    ...prevState[parentKey],
                    [childKey]: value,
                },
            }));
        } else {
            setEditedData((prevState) => ({
                ...prevState,
                [name]: type === 'checkbox' ? checked : value,
                ...(name === "pincode" && value === "" ? { city: "", state: "", country: "" } : {}),
            }));
        }

        setEditValidationErrors((prevErrors) => ({
            ...prevErrors,
            [name]: "",
        }));
    };

    const handleEditUnitChange = (fieldName, selectedOption) => {
        setEditedData(prev => ({
            ...prev,
            [fieldName]: selectedOption ? selectedOption.value : ''
        }));
    }

    const handleAddEditItem = () => {
        setEditedData((prevData) => ({
            ...prevData,
            platForms: [
                ...prevData.platForms,
                { name: "", webLink: "" },
            ],
        }));
    };

    const handleRemoveEditItem = (index) => {
        setEditedData((prevData) => {
            const updatedItems = [...prevData.platForms];
            updatedItems.splice(index, 1);
            return {
                ...prevData,
                platForms: updatedItems,
            };
        });
    };

    const getEditPostalCodeDetails = async () => {
        if (!editedData.pincode || editedData.pincode.length < 6) return;
        
        try {
            const response = await axios.get(
                `https://api.postalpincode.in/pincode/${editedData.pincode}`
            );

            if (response?.data[0]?.Status === "Success") {
                const postOffice = response?.data[0]?.PostOffice[0];
                setEditedData((prevData) => ({
                    ...prevData,
                    city: postOffice?.Division || "",
                    state: postOffice?.State || "",
                    country: postOffice?.Country || "",
                }));
                toast.success("Location details updated!");
            } else if (response?.data[0]?.Status === "Error") {
                toast.warning("Invalid Pincode. Please enter manually.");
            }
        } catch (error) {
            // Silent fail - user can enter manually
        }
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        setIsEditBtnLoading(true);
        try {
            const formData = new FormData();
            
            // Basic Info
            formData.append("_id", editedData._id);
            formData.append("name", editedData.name);
            formData.append("phone", editedData.phone);
            formData.append("email", editedData.email);
            formData.append("type", editedData.type);
            
            // Address
            formData.append("address", editedData.address);
            formData.append("pincode", editedData.pincode);
            formData.append("city", editedData.city);
            formData.append("state", editedData.state);
            formData.append("country", editedData.country);
            
            // Location
            formData.append("coordinates[latitude]", editedData.latitude);
            formData.append("coordinates[longitude]", editedData.longitude);
            
            // Operation
            formData.append("orderAcceptType", editedData.orderAcceptType);
            formData.append("menuGroup", editedData.menuGroup);
            formData.append("openingTime", editedData.openingTime || null);
            formData.append("closingTime", editedData.closingTime || null);
            formData.append("isOutletClosed", editedData.isOutletClosed);
            formData.append("isEventOutlet", editedData.isEventOutlet);
            formData.append("status", editedData?.status);
            
            // Business
            formData.append("gstin", editedData.gstin);
            formData.append("fssai", editedData.fssai);
            
            // Units
            if(editedData.foodUnitId) formData.append("foodUnitId", editedData.foodUnitId);
            if(editedData.beverageUnitId) formData.append("beverageUnitId", editedData.beverageUnitId);            
            if(editedData.centralUnitId) formData.append("centralUnitId", editedData.centralUnitId);                
            
            // Payment
            formData.append("paymentType", editedData.paymentType);
            if (editedData.paymentType === "CARD_SWIPE") {
                formData.append("pineLabCredentials[merchantId]", editedData.pineLabCredentials.merchantId);
                formData.append("pineLabCredentials[securityToken]", editedData.pineLabCredentials.securityToken);
                formData.append("pineLabCredentials[clientId]", editedData.pineLabCredentials.clientId);
                formData.append("pineLabCredentials[storeId]", editedData.pineLabCredentials.storeId);
            }
            
            // Delivery Radius
            if (editedData.minimumDeliveryRadius) {
                formData.append("minimumDeliveryRadius", editedData.minimumDeliveryRadius);
            }

            // Platforms
            if (editedData?.platForms.length > 0) {
                editedData.platForms.forEach((platform, index) => {
                    if (platform.name?.trim() && platform.webLink?.trim()) {
                        formData.append(`platForms[${index}][name]`, platform.name);
                        formData.append(`platForms[${index}][webLink]`, platform.webLink);
                        if (platform._id) {
                            formData.append(`platForms[${index}][_id]`, platform._id);
                        }
                    }
                });
            }

            const headers = {
                "Content-Type": "multipart/form-data",
                Authorization: `Bearer ${accessToken}`,
            };

            const response = await axios.put("outlet", formData, { headers });
            
            if (response?.data?.status === true) {
                toast.success("Outlet updated successfully!");
                navigate('/outlet-management/outlets');
            }
        } catch (error) {
            console.log("Error in Updating :", error);
            const errorMessage = error?.response?.data?.error?.message || 
                                error?.response?.data?.error || 
                                "Failed to update outlet";
            toast.error(errorMessage);
        } finally {
            setIsEditBtnLoading(false);
        }
    };

    // Check if form is valid for submission
    const isFormValid = () => {
        return (
            editedData.name &&
            editedData.phone &&
            editedData.email &&
            editedData.address &&
            editedData.pincode &&
            editedData.city &&
            editedData.state &&
            editedData.country &&
            editedData.latitude &&
            editedData.longitude &&
            editedData.menuGroup &&
            editedData.type &&
            editedData.orderAcceptType &&
            editedData.gstin &&
            editedData.paymentType &&
            editedData.status
        );
    };

    useEffect(() => {
        getMenuGroup();
        getProcurementUnits();
    }, []);

    useEffect(() => {
        if (editedData.paymentType === "PAYMENT_GATEWAY") {
            setEditedData((prev) => ({
                ...prev,
                pineLabCredentials: {
                    merchantId: "",
                    clientId: "",
                    securityToken: "",
                    storeId: ""
                }
            }));
        }
    }, [editedData.paymentType]);

    return (
        <React.Fragment>
            <Header onSkin={setSkin} />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid px-0">
                    {/* Header Card */}
                    <Card className="shadow-sm border-0 mb-3 bg-white">
                        <Card.Body className="py-3">
                            <Row className="align-items-center">
                                <Col>
                                    <h5 className="fw-bold mb-0">
                                        <i className="ri-edit-box-line me-2 text-primary"></i>
                                        EDIT OUTLET DETAILS
                                    </h5>
                                    <p className="text-muted small mb-0 mt-1">
                                        Update the outlet information below
                                    </p>
                                </Col>
                                <Col className="text-end">
                                    <Button 
                                        variant="outline-secondary" 
                                        size="sm"
                                        style={{ width: '100px' }}
                                        onClick={() => navigate('/outlet-management/outlets')}
                                    >
                                        <i className="ri-arrow-left-line me-1"></i>
                                        Back
                                    </Button>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Status Badge */}
                    <Card className="border-0 mb-3 bg-info shadow-sm text-white">
                        <Card.Body className="py-2">
                            <Row className="align-items-center">
                                <Col>
                                    <span className="small fw-semibold text-white">Outlet Name: </span>
                                    <span className="small fw-semibold">{editedData.name}</span>
                                </Col>
                                <Col className="text-end">
                                    <span className={`badge ${editedData.status === 'ACTIVE' ? 'bg-success' : 
                                        editedData.status === 'INACTIVE' ? 'bg-danger' : 'bg-warning'}`}>
                                        {editedData.status || 'Not Set'}
                                    </span>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Progress Steps */}
                    <Row className="mb-3 g-2">
                        {['Basic', 'Location', 'Operation', 'Business', 'Payment'].map((step, idx) => (
                            <Col key={idx}>
                                <div 
                                    className={`text-center shadow-sm fw-semibold p-2 rounded ${parseInt(activeAccordion) === idx ? 'bg-primary text-white' : 'bg-white'}`} 
                                    style={{ fontSize: '0.8rem', cursor: 'pointer' }}
                                    onClick={() => setActiveAccordion(idx.toString())}
                                >
                                    {step}
                                </div>
                            </Col>
                        ))}
                    </Row>

                    <Form onSubmit={handleEditSubmit}>
                        <Accordion activeKey={activeAccordion} onSelect={(key) => setActiveAccordion(key)} alwaysOpen={false}>
                            {/* Section 1: Basic Information */}
                            <Accordion.Item eventKey="0" className="border-0 mb-3 shadow-sm">
                                <Accordion.Header className="bg-white">
                                    <div className="d-flex align-items-center">
                                        <div className="bg-primary bg-opacity-10 p-2 rounded me-3">
                                            <i className="ri-information-line text-primary"></i>
                                        </div>
                                        <div>
                                            <h6 className="mb-0 fw-bold">Basic Information</h6>
                                            <small className="text-muted">Outlet name, contact, and status</small>
                                        </div>
                                    </div>
                                </Accordion.Header>
                                <Accordion.Body className="pt-3">
                                    <Row className="g-2">
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">Outlet Name <span className="text-danger">*</span></Form.Label>
                                                <Form.Control
                                                    size="sm"
                                                    type="text"
                                                    placeholder="e.g., Downtown Cafe"
                                                    name="name"
                                                    value={editedData.name}
                                                    onChange={handleEditChange}
                                                    required
                                                />
                                                {editValidationErrors.name && (
                                                    <Form.Text className="text-danger small">{editValidationErrors.name}</Form.Text>
                                                )}
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">Email <span className="text-danger">*</span></Form.Label>
                                                <Form.Control
                                                    size="sm"
                                                    type="email"
                                                    placeholder="outlet@example.com"
                                                    name="email"
                                                    value={editedData.email}
                                                    onChange={handleEditChange}
                                                    required
                                                />
                                                {editValidationErrors.email && (
                                                    <Form.Text className="text-danger small">{editValidationErrors.email}</Form.Text>
                                                )}
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">Phone <span className="text-danger">*</span></Form.Label>
                                                <Form.Control
                                                    size="sm"
                                                    type="tel"
                                                    placeholder="+91 98765 43210"
                                                    name="phone"
                                                    value={editedData.phone}
                                                    onChange={handleEditChange}
                                                    required
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">Outlet Type <span className="text-danger">*</span></Form.Label>
                                                <Form.Select
                                                    size="sm"
                                                    name="type"
                                                    value={editedData.type}
                                                    onChange={handleEditChange}
                                                    required
                                                >
                                                    <option value="">Select type</option>
                                                    <option value="COMPANY_OWNED">Company Owned</option>
                                                    <option value="FRANCHISE">Franchise</option>
                                                    <option value="EVENT">Event</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">Menu Group <span className="text-danger">*</span></Form.Label>
                                                <Form.Select
                                                    size="sm"
                                                    name="menuGroup"
                                                    value={editedData.menuGroup}
                                                    onChange={handleEditChange}
                                                    required
                                                >
                                                    <option value="">Select menu</option>
                                                    {groups.map(group => (
                                                        <option key={group._id} value={group._id}>{group.groupName}</option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">Status <span className="text-danger">*</span></Form.Label>
                                                <Form.Select
                                                    size="sm"
                                                    name="status"
                                                    value={editedData.status}
                                                    onChange={handleEditChange}
                                                    required
                                                >
                                                    <option value="">Select status</option>
                                                    <option value="ACTIVE">Active</option>
                                                    <option value="INACTIVE">Inactive</option>
                                                    <option value="COMING_SOON">Coming Soon</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Accordion.Body>
                            </Accordion.Item>

                            {/* Section 2: Location */}
                            <Accordion.Item eventKey="1" className="border-0 mb-3 shadow-sm">
                                <Accordion.Header>
                                    <div className="d-flex align-items-center">
                                        <div className="bg-primary bg-opacity-10 p-2 rounded me-3">
                                            <i className="ri-map-pin-line text-primary"></i>
                                        </div>
                                        <div>
                                            <h6 className="mb-0 fw-bold">Location</h6>
                                            <small className="text-muted">Address and coordinates</small>
                                        </div>
                                    </div>
                                </Accordion.Header>
                                <Accordion.Body className="pt-3">
                                    <Row className="g-2 mb-2">
                                        <Col md={8}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">Address <span className="text-danger">*</span></Form.Label>
                                                <Form.Control
                                                    size="sm"
                                                    as="textarea"
                                                    rows={2}
                                                    placeholder="Street address, landmark, etc."
                                                    name="address"
                                                    value={editedData.address}
                                                    onChange={handleEditChange}
                                                    required
                                                />
                                                {editValidationErrors.address && (
                                                    <Form.Text className="text-danger small">{editValidationErrors.address}</Form.Text>
                                                )}
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">Pincode <span className="text-danger">*</span></Form.Label>
                                                <Form.Control
                                                    size="sm"
                                                    type="text"
                                                    placeholder="6-digit pincode"
                                                    name="pincode"
                                                    value={editedData.pincode}
                                                    onChange={handleEditChange}
                                                    onBlur={getEditPostalCodeDetails}
                                                    maxLength={6}
                                                    required
                                                />
                                                <Form.Text className="text-muted small">Auto-fills city/state</Form.Text>
                                                {editValidationErrors.pincode && (
                                                    <Form.Text className="text-danger small">{editValidationErrors.pincode}</Form.Text>
                                                )}
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    
                                    <Row className="g-2 mb-2">
                                        <Col md={3}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">City <span className="text-danger">*</span></Form.Label>
                                                <Form.Control
                                                    size="sm"
                                                    type="text"
                                                    placeholder="City"
                                                    name="city"
                                                    value={editedData.city}
                                                    onChange={handleEditChange}
                                                    required
                                                />
                                                {editValidationErrors.city && (
                                                    <Form.Text className="text-danger small">{editValidationErrors.city}</Form.Text>
                                                )}
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">State <span className="text-danger">*</span></Form.Label>
                                                <Form.Control
                                                    size="sm"
                                                    type="text"
                                                    placeholder="State"
                                                    name="state"
                                                    value={editedData.state}
                                                    onChange={handleEditChange}
                                                    required
                                                />
                                                {editValidationErrors.state && (
                                                    <Form.Text className="text-danger small">{editValidationErrors.state}</Form.Text>
                                                )}
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">Country <span className="text-danger">*</span></Form.Label>
                                                <Form.Control
                                                    size="sm"
                                                    type="text"
                                                    placeholder="Country"
                                                    name="country"
                                                    value={editedData.country}
                                                    onChange={handleEditChange}
                                                    required
                                                />
                                                {editValidationErrors.country && (
                                                    <Form.Text className="text-danger small">{editValidationErrors.country}</Form.Text>
                                                )}
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">Latitude <span className="text-danger">*</span></Form.Label>
                                                <Form.Control
                                                    size="sm"
                                                    type="number"
                                                    step="any"
                                                    placeholder="e.g., 28.6139"
                                                    name="latitude"
                                                    value={editedData.latitude}
                                                    onChange={handleEditChange}
                                                    required
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Row className="g-2">
                                        <Col md={3}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">Longitude <span className="text-danger">*</span></Form.Label>
                                                <Form.Control
                                                    size="sm"
                                                    type="number"
                                                    step="any"
                                                    placeholder="e.g., 77.2090"
                                                    name="longitude"
                                                    value={editedData.longitude}
                                                    onChange={handleEditChange}
                                                    required
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Accordion.Body>
                            </Accordion.Item>

                            {/* Section 3: Operation */}
                            <Accordion.Item eventKey="2" className="border-0 mb-3 shadow-sm">
                                <Accordion.Header>
                                    <div className="d-flex align-items-center">
                                        <div className="bg-primary bg-opacity-10 p-2 rounded me-3">
                                            <i className="ri-timer-line text-primary"></i>
                                        </div>
                                        <div>
                                            <h6 className="mb-0 fw-bold">Operation</h6>
                                            <small className="text-muted">Timings and delivery settings</small>
                                        </div>
                                    </div>
                                </Accordion.Header>
                                <Accordion.Body className="pt-3">
                                    <Row className="g-2">
                                        <Col md={3}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">Order Type <span className="text-danger">*</span></Form.Label>
                                                <Form.Select
                                                    size="sm"
                                                    name="orderAcceptType"
                                                    value={editedData.orderAcceptType}
                                                    onChange={handleEditChange}
                                                    required
                                                >
                                                    <option value="">Select</option>
                                                    <option value="ALL">All Orders</option>
                                                    <option value="PICKUP">Pickup Only</option>
                                                    <option value="DELIVERY">Delivery Only</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">Opening Time</Form.Label>
                                                <Form.Control
                                                    size="sm"
                                                    type="time"
                                                    name="openingTime"
                                                    value={editedData.openingTime}
                                                    onChange={handleEditChange}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">Closing Time</Form.Label>
                                                <Form.Control
                                                    size="sm"
                                                    type="time"
                                                    name="closingTime"
                                                    value={editedData.closingTime}
                                                    onChange={handleEditChange}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">Delivery Radius (km)</Form.Label>
                                                <Form.Control
                                                    size="sm"
                                                    type="number"
                                                    min="0"
                                                    step="0.5"
                                                    placeholder="e.g., 5"
                                                    name="minimumDeliveryRadius"
                                                    value={editedData.minimumDeliveryRadius}
                                                    onChange={handleEditChange}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Row className="g-2 mt-2">
                                        <Col md={3}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">Outlet Closed?</Form.Label>
                                                <div className="d-flex align-items-center">
                                                    <Form.Check
                                                        type="switch"
                                                        id="outletClosedSwitch"
                                                        onChange={handleEditChange}
                                                        checked={editedData?.isOutletClosed}
                                                        name="isOutletClosed"
                                                    />
                                                    <span className="ms-2 small">{editedData?.isOutletClosed ? 'Yes' : 'No'}</span>
                                                </div>
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">Event Outlet?</Form.Label>
                                                <div className="d-flex align-items-center">
                                                    <Form.Check
                                                        type="switch"
                                                        id="eventOutletSwitch"
                                                        onChange={handleEditChange}
                                                        checked={editedData?.isEventOutlet}
                                                        name="isEventOutlet"
                                                    />
                                                    <span className="ms-2 small">{editedData?.isEventOutlet ? 'Yes' : 'No'}</span>
                                                </div>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Accordion.Body>
                            </Accordion.Item>

                            {/* Section 4: Business */}
                            <Accordion.Item eventKey="3" className="border-0 mb-3 shadow-sm">
                                <Accordion.Header>
                                    <div className="d-flex align-items-center">
                                        <div className="bg-primary bg-opacity-10 p-2 rounded me-3">
                                            <i className="ri-briefcase-line text-primary"></i>
                                        </div>
                                        <div>
                                            <h6 className="mb-0 fw-bold">Business</h6>
                                            <small className="text-muted">GST, FSSAI, and units</small>
                                        </div>
                                    </div>
                                </Accordion.Header>
                                <Accordion.Body className="pt-3">
                                    <Row className="g-2 mb-3">
                                        <Col md={3}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">GSTIN <span className="text-danger">*</span></Form.Label>
                                                <Form.Control
                                                    size="sm"
                                                    type="text"
                                                    placeholder="22AAAAA0000A1Z5"
                                                    name="gstin"
                                                    value={editedData.gstin}
                                                    onChange={handleEditChange}
                                                    required
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">FSSAI</Form.Label>
                                                <Form.Control
                                                    size="sm"
                                                    type="text"
                                                    placeholder="FSSAI license number"
                                                    name="fssai"
                                                    value={editedData.fssai}
                                                    onChange={handleEditChange}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Row className="g-2">
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">Food Unit</Form.Label>
                                                <Select
                                                    options={foodUnitOptions}
                                                    value={foodUnitOptions?.find(option => option.value === editedData.foodUnitId)}
                                                    onChange={(selectedOption) => handleEditUnitChange('foodUnitId', selectedOption)}
                                                    placeholder="Select Food Unit"
                                                    isSearchable
                                                    components={animatedComponents}
                                                    styles={customSelectStyles}
                                                    isClearable
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">Beverage Unit</Form.Label>
                                                <Select
                                                    options={beverageUnitOptions}
                                                    value={beverageUnitOptions?.find(option => option.value === editedData.beverageUnitId)}
                                                    onChange={(selectedOption) => handleEditUnitChange('beverageUnitId', selectedOption)}
                                                    placeholder="Select Beverage Unit"
                                                    isSearchable
                                                    components={animatedComponents}
                                                    styles={customSelectStyles}
                                                    isClearable
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">Central Unit</Form.Label>
                                                <Select
                                                    options={centralUnitOptions}
                                                    value={centralUnitOptions?.find(option => option.value === editedData.centralUnitId)}
                                                    onChange={(selectedOption) => handleEditUnitChange('centralUnitId', selectedOption)}
                                                    placeholder="Select Central Unit"
                                                    isSearchable
                                                    styles={customSelectStyles}
                                                    components={animatedComponents}
                                                    isClearable
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Accordion.Body>
                            </Accordion.Item>

                            {/* Section 5: Payment & Platforms */}
                            <Accordion.Item eventKey="4" className="border-0 mb-3 shadow-sm">
                                <Accordion.Header>
                                    <div className="d-flex align-items-center">
                                        <div className="bg-primary bg-opacity-10 p-2 rounded me-3">
                                            <i className="ri-bank-card-line text-primary"></i>
                                        </div>
                                        <div>
                                            <h6 className="mb-0 fw-bold">Payment & Platforms</h6>
                                            <small className="text-muted">Payment method and delivery platforms</small>
                                        </div>
                                    </div>
                                </Accordion.Header>
                                <Accordion.Body className="pt-3">
                                    <Row className="g-2 mb-3">
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">Payment Method <span className="text-danger">*</span></Form.Label>
                                                <Form.Select
                                                    size="sm"
                                                    name="paymentType"
                                                    value={editedData.paymentType}
                                                    onChange={handleEditChange}
                                                    required
                                                >
                                                    <option value="">Select method</option>
                                                    <option value="PAYMENT_GATEWAY">Razor Pay</option>
                                                    <option value="CARD_SWIPE">Pine Labs Machine</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    {editedData.paymentType === "CARD_SWIPE" && (
                                        <Card className="bg-light border-0 mb-3">
                                            <Card.Body className="p-3">
                                                <h6 className="small fw-bold mb-2">Pine Labs Credentials</h6>
                                                <Row className="g-2">
                                                    <Col md={3}>
                                                        <Form.Control
                                                            size="sm"
                                                            type="text"
                                                            placeholder="Merchant ID"
                                                            name="pineLabCredentials.merchantId"
                                                            value={editedData?.pineLabCredentials?.merchantId}
                                                            onChange={handleEditChange}
                                                            required
                                                        />
                                                    </Col>
                                                    <Col md={3}>
                                                        <Form.Control
                                                            size="sm"
                                                            type="text"
                                                            placeholder="Security Token"
                                                            name="pineLabCredentials.securityToken"
                                                            value={editedData?.pineLabCredentials?.securityToken}
                                                            onChange={handleEditChange}
                                                            required
                                                        />
                                                    </Col>
                                                    <Col md={3}>
                                                        <Form.Control
                                                            size="sm"
                                                            type="text"
                                                            placeholder="Client ID"
                                                            name="pineLabCredentials.clientId"
                                                            value={editedData?.pineLabCredentials?.clientId}
                                                            onChange={handleEditChange}
                                                            required
                                                        />
                                                    </Col>
                                                    <Col md={3}>
                                                        <Form.Control
                                                            size="sm"
                                                            type="text"
                                                            placeholder="Store ID"
                                                            name="pineLabCredentials.storeId"
                                                            value={editedData?.pineLabCredentials?.storeId}
                                                            onChange={handleEditChange}
                                                            required
                                                        />
                                                    </Col>
                                                </Row>
                                            </Card.Body>
                                        </Card>
                                    )}

                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <h6 className="small fw-bold mb-0">Delivery Platforms</h6>
                                        <Button
                                            size="sm"
                                            variant="outline-primary"
                                            onClick={handleAddEditItem}
                                        >
                                            <i className="ri-add-line me-1"></i>
                                            Add Platform
                                        </Button>
                                    </div>

                                    {(Array.isArray(editedData?.platForms) && editedData?.platForms.length > 0 ? editedData.platForms : [{ name: '', webLink: '' }]).map((platform, index) => (
                                        <Card key={index} className="bg-light border-0 mb-2">
                                            <Card.Body className="p-2">
                                                <Row className="g-2 align-items-center">
                                                    <Col md={5}>
                                                        <Form.Control
                                                            size="sm"
                                                            type="text"
                                                            placeholder="Platform name (e.g., Zomato)"
                                                            name="platformName"
                                                            value={platform.name}
                                                            onChange={(e) => handleEditChange(e, index)}
                                                        />
                                                    </Col>
                                                    <Col md={5}>
                                                        <Form.Control
                                                            size="sm"
                                                            type="text"
                                                            placeholder="https://example.com"
                                                            name="platformWeblink"
                                                            value={platform.webLink}
                                                            onChange={(e) => handleEditChange(e, index)}
                                                        />
                                                    </Col>
                                                    <Col md={2}>
                                                        <div className="d-flex gap-1">
                                                            {index === (editedData?.platForms?.length || 1) - 1 && (
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline-success"
                                                                    onClick={handleAddEditItem}
                                                                    className="btn-sm"
                                                                >
                                                                    <i className="ri-add-line"></i>
                                                                </Button>
                                                            )}
                                                            {(editedData?.platForms?.length || 1) > 1 && (
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline-danger"
                                                                    onClick={() => handleRemoveEditItem(index)}
                                                                    className="btn-sm"
                                                                >
                                                                    <i className="ri-delete-bin-line"></i>
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </Card.Body>
                                        </Card>
                                    ))}
                                </Accordion.Body>
                            </Accordion.Item>
                        </Accordion>

                        {/* Form Actions */}
                        <Card className="border-0 shadow-sm mt-3">
                            <Card.Body className="py-3">
                                <Row className="align-items-center">
                                    <Col>
                                        <div className="d-flex align-items-center">
                                            <i className="ri-information-line text-primary me-2"></i>
                                            <small className="text-muted">
                                                Fields marked with <span className="text-danger">*</span> are required
                                            </small>
                                        </div>
                                    </Col>
                                    <Col className="text-end">
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            className="me-2"
                                            onClick={() => navigate('/outlet-management/outlets')}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            type="submit"
                                            variant="primary"
                                            size="sm"
                                            disabled={isEditBtnLoading || !isFormValid()}
                                            className="px-4"
                                        >
                                            {isEditBtnLoading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" />
                                                    Saving...
                                                </>
                                            ) : (
                                                'Save Changes'
                                            )}
                                        </Button>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Form>
                </div>
            </div>
        </React.Fragment>
    );
};

export default EditOutlet;